import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'

import type { Ref, VNodeRef } from 'vue'
import { computed, onBeforeUpdate, ref, watch } from 'vue'

const SELECTION_LENGTH_LOWER_LIMIT = 2
const createInitialSelections = () =>
  Array.from({ length: SELECTION_LENGTH_LOWER_LIMIT }, () => '')

export const useSelectionManipulator = (selections: Ref<string[]>) => {
  const isSelectionsLowerThanLimit = computed(
    () => selections.value.length <= SELECTION_LENGTH_LOWER_LIMIT
  )

  watch(
    selections,
    (newSelections: string[]) => {
      // 不正な値だったら正しい値に直す
      if (
        newSelections.length !== 0 &&
        newSelections.length < SELECTION_LENGTH_LOWER_LIMIT
      ) {
        selections.value = createInitialSelections().map(
          (value, index) => newSelections[index] ?? value
        )
      }
    },
    { immediate: true }
  )

  /**
   * 選択肢を追加する
   */
  const appendSelection = () => {
    selections.value =
      selections.value.length > 0
        ? [...selections.value, '']
        : createInitialSelections()
  }

  /**
   * 選択肢を全て削除する
   */
  const deleteAllSelections = () => {
    selections.value = []
  }

  /**
   * 特定の選択肢を削除する
   * @param index 削除するitemのindex
   */
  const deleteSelection = (index: number) => {
    if (isSelectionsLowerThanLimit.value) {
      return
    }
    selections.value = [
      ...selections.value.slice(0, index),
      ...selections.value.slice(index + 1),
    ]
  }

  /**
   * 途中に選択肢を挿入する
   * @param indexAfter 挿入するひとつ前のindex
   * @param value 挿入する値
   */
  const injectSelection = (index: number, value?: string) => {
    selections.value = [
      ...selections.value.slice(0, index),
      value ?? '',
      ...selections.value.slice(index),
    ]
  }

  /**
   * 途中に選択肢を複数挿入する
   * @param index
   * @param values
   */
  const injectSelections = (index: number, values: string[]) => {
    selections.value = values.reduce<string[]>(
      (acc, value, currentValuesIndex): string[] => {
        const modifyingIndex = index + currentValuesIndex
        if (acc.length <= modifyingIndex) {
          // レンジ外への挿入
          return [...acc, value]
        }
        if (acc[modifyingIndex] === '') {
          // 空文字への挿入
          return [
            ...acc.slice(0, modifyingIndex),
            value,
            ...acc.slice(modifyingIndex + 1),
          ]
        }
        // 途中に選択肢を挿入
        return [
          ...acc.slice(0, modifyingIndex),
          value,
          ...acc.slice(modifyingIndex),
        ]
      },
      [...selections.value]
    )
  }

  /**
   * 選択肢のvalueを更新する
   * @param index 更新するitemのindex
   * @param value 新しい値
   */
  const updateSelection = (index: number, value: string) => {
    selections.value = [
      ...selections.value.slice(0, index),
      value,
      ...selections.value.slice(index + 1),
    ]
  }

  return {
    appendSelection,
    deleteAllSelections,
    deleteSelection,
    injectSelection,
    injectSelections,
    updateSelection,
  }
}

export const useSelectionInputFocus = () => {
  /**
   * v-forのrefは順番が保証されないらしく、
   * 内部実装がindexで参照すること、refを取る時に順不同で来ても良いように
   * あえてMap<index, InputEl>で持っている
   */
  const inputRefs = ref<Map<number, HTMLInputElement>>(new Map())
  onBeforeUpdate(() => {
    /**
     * 該当コンポーネントの DOM 更新時には
     * inputRefs の watcher が反応するように再代入で初期化
     */
    inputRefs.value = new Map()
  })

  /**
   * v-forでrefを取るための関数
   * @param el HTMLInputElement
   */
  const setInputRef = (index: number, el: HTMLInputElement | null) => {
    if (el == null) {
      return
    }
    /**
     * NOTE: set時、あえて破壊的メソッドで watcher を迂回する実装にしないと無限ループする
     * vueuse の useTemplateRefsList でも同様の watcher 迂回する実装(Array.prototype.push())がある
     *
     * ref. https://github.com/vueuse/vueuse/blob/b7b985b198d9e72136f65fae39b2125cd3abef5b/packages/core/useTemplateRefsList/index.ts#L12
     */
    inputRefs.value.set(index, el)
  }

  /**
   * 特定の選択肢のinputにフォーカスする
   * @param index
   */
  const focusInput = (index: number) => {
    inputRefs.value.get(index)?.focus()
  }

  /**
   * v-for で DolphinTextInput の input の ref を取るための helper 関数
   * @param index
   */
  const setDolphinTextInputRef = (index: number) =>
    ((instance: InstanceType<typeof DolphinTextInput> | null) => {
      if (instance == null) {
        return
      }
      setInputRef(index, instance.inputRef)
    }) as unknown as VNodeRef

  return {
    setDolphinTextInputRef,
    setInputRef,
    focusInput,
  }
}

export const useSelectionTabIndex = () => {
  const currentFocusIndex = ref<number | null>(null)

  /**
   * フォーカスしているinputを取得するイベントハンドラ
   * @param index
   */
  const onInputFocus = (index: number) => {
    currentFocusIndex.value = index
  }

  /**
   * inputからフォーカスが外れていることを取得するイベントハンドラ
   */
  const onInputBlur = () => {
    currentFocusIndex.value = null
  }

  /**
   * inputのtabindexを取得する
   * @param index
   */
  const getInputTabIndex = (index: number): string | undefined => {
    if (currentFocusIndex.value && index === currentFocusIndex.value + 1) {
      return '0'
    }

    return undefined
  }

  /**
   * closeボタンのtabindexを取得する
   * @param index
   */
  const getCloseButtonTabIndex = (index: number): string | undefined => {
    if (!currentFocusIndex.value) {
      return undefined
    }

    if (
      index === currentFocusIndex.value ||
      index === currentFocusIndex.value - 1
    ) {
      return '-1'
    }

    return undefined
  }

  return {
    onInputFocus,
    onInputBlur,
    getInputTabIndex,
    getCloseButtonTabIndex,
  }
}
